/**
 * vingreact#989: the consume STATE PUT and its "slow failure".
 *
 * The state PUT (`PUT /api/view/shares/{stub}/`) is the learner's autosave. A
 * slow server does not cancel its work when the client gives up, and a 504
 * from the edge (CloudFront answers at 30 s) does not mean the save failed:
 * the handler keeps running and may commit afterwards. Re-sending such a save
 * queues a second copy behind the first on the server (the 09-29 group start).
 *
 * A SLOW failure is a state PUT that failed with a client timeout, or with a
 * 504 received at least `SLOW_504_MS` after THAT attempt started. A fast 504
 * (the edge could not reach the origin, e.g. a deploy) is not slow.
 *
 * Dependency-light on purpose (no import of `client.ts`): the interceptor's
 * retry rule and the persist hook (989 S2a) both read it.
 */
import axios from 'axios';
import { isTransportTimeout } from '@/api/failureClass';

/** A 504 at or after this many ms from the attempt's start came from a slow origin. */
export const SLOW_504_MS = 25_000;

/**
 * The per-attempt start stamp, set on EVERY attempt by the request interceptor
 * (a transport retry re-stamps it) and by `keepaliveRequest`. Distinct from the
 * interceptor's `__startTime`, which survives retries on purpose.
 */
export interface AttemptStamped {
  __attemptStart?: number;
}

const STATE_PUT_PATH = /^(?:https?:\/\/[^/]+)?\/api\/view\/shares\/[^/?#]+\/$/;

/**
 * Exactly the consume state PUT: `PUT /api/view/shares/{stub}/`, with or
 * without a query string. Not the file-accept POST, not any other sub-path,
 * not a GET.
 */
export function isConsumeStatePut(method: string, url: string): boolean {
  if (method.toUpperCase() !== 'PUT') return false;
  const path = url.split(/[?#]/)[0] ?? url;
  return STATE_PUT_PATH.test(path);
}

/**
 * Did this consume state PUT fail while the server may still be working on it?
 * True for a client timeout, or a 504 received >= `SLOW_504_MS` after the
 * attempt's `__attemptStart`. False for everything else, including any other
 * route and a 504 whose attempt start is unknown.
 */
export function isSlowConsumeFailure(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const config = error.config as (typeof error.config & AttemptStamped) | undefined;
  if (!config || !isConsumeStatePut(config.method ?? '', config.url ?? '')) return false;
  if (isTransportTimeout(error)) return true;
  if (error.response?.status !== 504) return false;
  const start = config.__attemptStart;
  return typeof start === 'number' && Date.now() - start >= SLOW_504_MS;
}
